"use client";

import { useEffect, useRef, useState } from "react";
import { useTheme } from "next-themes";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

const MAP_WAYPOINTS = [
  {
    center: [-79.42325515100308, 43.641882280805135],
    zoom: 16.7,
    pitch: 75,
    bearing: 330,
  },
  {
    center: [-79.3817, 43.6441],
    zoom: 16.8,
    pitch: 76,
    bearing: 292,
  },
  {
    center: [-79.395, 43.6677],
    zoom: 16.5,
    pitch: 72,
    bearing: 348,
  },
];

const TORONTO_INITIAL = {
  center: [-79.42325515100308, 43.641882280805135],
  zoom: 15,
  pitch: 30,
  bearing: 330,
};

function add3DBuildings(mapInstance) {
  if (mapInstance.getLayer("blog-hero-3d-buildings")) return;

  mapInstance.addLayer({
    id: "blog-hero-3d-buildings",
    source: "composite",
    "source-layer": "building",
    filter: ["==", "extrude", "true"],
    type: "fill-extrusion",
    minzoom: 15,
    paint: {
      "fill-extrusion-color": [
        "interpolate",
        ["linear"],
        ["get", "height"],
        0,
        "#5f86b7",
        70,
        "#3b82f6",
        130,
        "#2563eb",
        190,
        "#1d4ed8",
      ],
      "fill-extrusion-height": ["get", "height"],
      "fill-extrusion-base": ["get", "min_height"],
      "fill-extrusion-opacity": 0.72,
      "fill-extrusion-vertical-gradient": true,
    },
  });
}

function simplifyMapStyle(mapInstance) {
  [
    "poi",
    "poi-label",
    "transit",
    "transit-label",
    "road-label",
    "water-point-label",
    "water-line-label",
    "natural-point-label",
    "natural-line-label",
    "waterway-label",
  ].forEach((layerId) => {
    if (mapInstance.getLayer(layerId)) mapInstance.removeLayer(layerId);
  });

  ["place-city", "place-town", "place-village"].forEach((layerId) => {
    if (mapInstance.getLayer(layerId)) {
      mapInstance.setPaintProperty(layerId, "text-color", "#7db7ff");
      mapInstance.setPaintProperty(layerId, "text-halo-color", "#020716");
      mapInstance.setPaintProperty(layerId, "text-halo-width", 1.5);
    }
  });
}

export default function BlogHeroMap() {
  const mapNode = useRef(null);
  const motionLayer = useRef(null);
  const mapRef = useRef(null);
  const loopRef = useRef(null);
  const frameRef = useRef(null);
  const rotationRef = useRef(TORONTO_INITIAL.bearing);
  const waypointIndex = useRef(0);
  const { resolvedTheme } = useTheme();
  const [error, setError] = useState(false);

  useEffect(() => {
    const motionNode = motionLayer.current;
    if (!motionNode) return undefined;

    const updateParallax = () => {
      const progress = Math.min(window.scrollY / 820, 1);
      const opacity = 0.64 - progress * 0.34;
      motionNode.style.opacity = String(Math.max(0.24, opacity));
      motionNode.style.transform = `translate3d(0, ${progress * 112}px, 0) scale(${1.03 + progress * 0.04})`;
      frameRef.current = null;
    };

    const onScroll = () => {
      if (frameRef.current) return;
      frameRef.current = window.requestAnimationFrame(updateParallax);
    };

    updateParallax();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
    };
  }, [error]);

  useEffect(() => {
    const node = mapNode.current;
    if (typeof window === "undefined" || !node) return undefined;

    mapboxgl.accessToken = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
    if (!mapboxgl.accessToken || !mapboxgl.supported()) {
      setError(true);
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const mapStyle = "mapbox://styles/mapbox/satellite-streets-v12";

    const mapboxMap = new mapboxgl.Map({
      container: node,
      style: mapStyle,
      center: TORONTO_INITIAL.center,
      zoom: TORONTO_INITIAL.zoom,
      pitch: TORONTO_INITIAL.pitch,
      bearing: TORONTO_INITIAL.bearing,
      interactive: false,
      dragPan: false,
      scrollZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
      attributionControl: false,
    });

    mapRef.current = mapboxMap;

    const flyToNext = () => {
      if (prefersReducedMotion || !mapRef.current) return;
      waypointIndex.current = (waypointIndex.current + 1) % MAP_WAYPOINTS.length;
      const next = MAP_WAYPOINTS[waypointIndex.current];
      rotationRef.current = next.bearing;
      mapRef.current.flyTo({
        center: next.center,
        zoom: next.zoom,
        pitch: next.pitch,
        bearing: next.bearing,
        duration: 7600,
        essential: false,
        curve: 1.6,
        speed: 0.58,
        easing(t) {
          return t * (2 - t);
        },
      });
      loopRef.current = window.setTimeout(flyToNext, 8800);
    };

    mapboxMap.on("error", () => setError(true));
    mapboxMap.on("style.load", () => {
      simplifyMapStyle(mapboxMap);
      add3DBuildings(mapboxMap);
      if (prefersReducedMotion) return;

      loopRef.current = window.setTimeout(() => {
        mapboxMap.flyTo({
          center: MAP_WAYPOINTS[0].center,
          zoom: MAP_WAYPOINTS[0].zoom,
          pitch: MAP_WAYPOINTS[0].pitch,
          bearing: MAP_WAYPOINTS[0].bearing,
          duration: 8000,
          essential: false,
          curve: 1.6,
          speed: 0.6,
          easing(t) {
            return t * (2 - t);
          },
        });
        loopRef.current = window.setTimeout(flyToNext, 9000);
      }, 900);
    });

    return () => {
      if (loopRef.current) window.clearTimeout(loopRef.current);
      mapRef.current = null;
      mapboxMap.remove();
    };
  }, [resolvedTheme]);

  if (error) {
    return (
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          ref={motionLayer}
          className="absolute inset-[-18%] will-change-transform"
          style={{
            background:
              "radial-gradient(ellipse at 52% 18%, rgba(37,99,235,0.34), transparent 48%), linear-gradient(135deg, rgba(2,7,22,0.2), rgba(2,7,22,0.92))",
            filter: "saturate(1.05) contrast(1.05)",
            opacity: 0.5,
            WebkitMaskImage:
              "radial-gradient(ellipse at 50% 28%, black 0%, black 46%, rgba(0,0,0,0.62) 68%, transparent 92%)",
            maskImage:
              "radial-gradient(ellipse at 50% 28%, black 0%, black 46%, rgba(0,0,0,0.62) 68%, transparent 92%)",
          }}
        />
      </div>
    );
  }

  return (
    <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
      <div
        ref={motionLayer}
        className="absolute inset-[-18%] will-change-transform"
        style={{
          opacity: 0.64,
          filter: "saturate(1.02) contrast(1.08)",
          WebkitMaskImage:
            "radial-gradient(ellipse at 50% 28%, black 0%, black 46%, rgba(0,0,0,0.62) 68%, transparent 92%)",
          maskImage:
            "radial-gradient(ellipse at 50% 28%, black 0%, black 46%, rgba(0,0,0,0.62) 68%, transparent 92%)",
        }}
      >
        <div ref={mapNode} className="h-full w-full" />
      </div>
    </div>
  );
}
